// Money card (money-mode §1.2, §2.1; right column, in the slot agar gives its party card): the
// second door on the home screen. The play card (centre) is Free Play: FFA with bots, no login;
// this card is the way into money arenas. It is always on the home screen once the first
// /api/info answer is in; while the server has no money rooms (`/api/info.money` absent, as in
// production until crypto settlement exists) it shows a "COMING SOON" state: the title, a grey
// tag and one line, no stake buttons, nothing clickable, no error.
//
// States (from app.ts `money` flow + the polled /api/info.money):
// - soon: money off on this server (or /api/info unreachable): "COMING SOON".
// - list (off / stakeSelect): "Play for money", a TEST MONEY tag while the server runs the fake
//   settlement (`devBuyIn`), and one button per stake with the live head count of the room you
//   would join ("$1 arena · 3/30 playing", shown before any buy-in) or "full". Polled every 15 s
//   while the menu shows it. Picking a stake joins that arena (`openMoney` + `pickStake`).
// - joining: spinner, "Joining the $1 arena…".
// - room (ready / buying / spawning / inLife): "You are in the $1 arena", the live count, the
//   buy-in line, and "Back to free play" (disabled during a money life: cash out or die first) and
//   "Pick another stake".
// - waiting: the stake's arenas answered `full` (all taken, or the server's capacity guard,
//   architecture §6.3): "Every $1 arena is full." and "Trying again in N s…" while the app asks
//   again with backoff; Cancel goes back to the stake list. Not an error (no red text).
// - error: the reason (buy-in errors, ERROR 11–16, network, disconnected) with Back / Leave.
// - room / error also show an earlier life of this account still frozen in an arena (a dropped
//   socket, money-mode §5) with its value and auto-cash-out countdown (`flow.frozenLife`).
// - Every state (also "coming soon": accounts ship before money does) ends with the account row
//   (ui/menu/account.ts: Sign in, or who is signed in, the wallet and Sign out) while the server
//   offers sign-in. Money needs an account: a stake pressed while signed out (or without a
//   username) asks for sign-in and the username first (`account.requireAccount`).
//
// `moneyCardView` is DOM-free (unit-tested). Consumer: ui/menu/layout.ts.

import type { MoneyFlowError, MoneyFlowState } from '../../app.js';
import type { UiClickSource } from '../../events/bus.js';
import type { ApiMoneyInfo } from '../../net/money-api.js';
import { h, type ReadonlySignal, show, type Unsubscribe } from '../dom.js';
import { type AccountRow, type AccountRowDeps, createAccountRow } from './account.js';
import { type I18nKey, t } from '../i18n/t.js';

/** "$1", "$5", "$0.50". */
export function formatStake(usd: number): string {
  if (!Number.isFinite(usd) || usd <= 0) return '$0';
  return Number.isInteger(usd) ? `$${usd}` : `$${usd.toFixed(2)}`;
}

/** The entry value a stake spawns with, in USD (stake × (1 − rake)), formatted. */
export function entryValueText(stakeUsd: number, rake: number): string {
  const r = Number.isFinite(rake) && rake >= 0 && rake < 1 ? rake : 0;
  return `$${(Math.floor(stakeUsd * (1 - r) * 100) / 100).toFixed(2)}`;
}

/** The user-facing reason for a money-flow error. */
export function moneyErrorText(error: MoneyFlowError | null): string {
  if (!error) return t('money.error.network');
  switch (error.kind) {
    case 'buyIn':
      return t(`money.error.buyIn.${error.error}` as I18nKey);
    case 'server':
      return t(`money.error.${error.error}` as I18nKey);
    default:
      return t(`money.error.${error.kind}` as I18nKey);
  }
}

export type MoneyCardMode = 'hidden' | 'soon' | 'list' | 'joining' | 'waiting' | 'room' | 'error';

export interface MoneyStakeView {
  readonly usd: number;
  readonly label: string;
  /** "3/30 playing" or "full". */
  readonly players: string;
  readonly full: boolean;
  /** The stake of the arena you are in / joining. */
  readonly selected: boolean;
}

export interface MoneyCardInput {
  readonly flow: MoneyFlowState;
  /** The newest /api/info.money (polled), or null. */
  readonly info: ApiMoneyInfo | null;
  /**
   * The first /api/info answer (or failure) is in. Before it the card stays hidden, so a dev
   * server with money on does not flash "coming soon". Absent = true.
   */
  readonly loaded?: boolean;
  /** Humans playing in the joined arena (ROOM section), or null. */
  readonly roomPlayers: number | null;
  /** A money life is on screen (Leave is disabled). */
  readonly lifeActive: boolean;
  /** WELCOME cashOutKey ("Q"). */
  readonly cashOutKey: string;
  /** Touch device: the copy names the $ hold button instead of the key. */
  readonly touch?: boolean;
  /** Formats lamports for the frozen-life line ("$0.85"); absent = no line. */
  readonly formatValue?: (lamports: number) => string;
  /** performance.now() for the frozen life's and the full-retry countdowns. */
  readonly nowMs?: number;
}

/** The line about an earlier money life still frozen in an arena (or null). */
export function frozenLifeLine(m: MoneyCardInput): string | null {
  const f = m.flow.frozenLife;
  if (!f || !m.formatValue) return null;
  const left = Math.ceil((f.cashOutAtMs - (m.nowMs ?? 0)) / 1000);
  return left > 0
    ? t('money.frozenLife', { v: m.formatValue(f.value), s: left })
    : t('money.frozenLifeSoon', { v: m.formatValue(f.value) });
}

export interface MoneyCardView {
  readonly mode: MoneyCardMode;
  /** TEST MONEY tag (fake settlement). */
  readonly test: boolean;
  /** COMING SOON tag (money off on this server). */
  readonly soon: boolean;
  readonly blurb: string;
  readonly stakes: readonly MoneyStakeView[];
  /** room / joining: the status lines. */
  readonly status: readonly string[];
  readonly error: string | null;
  readonly canLeave: boolean;
}

/** What the money card shows (DOM-free). */
export function moneyCardView(m: MoneyCardInput): MoneyCardView {
  const info = m.info ?? m.flow.info;
  const step = m.flow.step;
  const enabled = info !== null && info.enabled && info.stakes.length > 0;
  const test = info?.devBuyIn === true;
  const key = m.cashOutKey || 'Q';
  const blurb =
    info?.game === 'arena'
      ? m.touch
        ? t('money.blurbArenaTouch')
        : t('money.blurbArena', { key })
      : m.touch
        ? t('money.blurbTouch')
        : t('money.blurb', { key });
  const stake = m.flow.stakeUsd;
  const stakes: MoneyStakeView[] = enabled
    ? info.stakes.map((usd) => {
        const pool = info.pools.find((p) => p.stakeUsd === usd);
        const full = pool?.full === true;
        return {
          usd,
          label: t('money.stakeItem', { stake: formatStake(usd) }),
          players: full
            ? t('money.stakeFull')
            : t('money.stakePlayers', { n: pool?.joinHumans ?? 0, max: pool?.maxPlayers ?? 0 }),
          full,
          selected: stake === usd,
        };
      })
    : [];
  const base = { test, soon: false, blurb, stakes, error: null, canLeave: !m.lifeActive };
  switch (step) {
    case 'off':
    case 'stakeSelect':
      if (enabled) return { ...base, mode: 'list', status: [] };
      if (m.loaded === false) return { ...base, mode: 'hidden', status: [] };
      return { ...base, mode: 'soon', soon: true, blurb: t('money.soonBlurb'), status: [] };
    case 'joining':
      return {
        ...base,
        mode: 'joining',
        status: [t('money.joining', { stake: formatStake(stake ?? 0) })],
      };
    case 'error': {
      const err = m.flow.error;
      if (err?.kind === 'full' && err.retryAtMs !== undefined) {
        const left = Math.ceil((err.retryAtMs - (m.nowMs ?? 0)) / 1000);
        return {
          ...base,
          mode: 'waiting',
          status: [
            t('money.fullWait', { stake: formatStake(stake ?? 0) }),
            left > 0 ? t('money.fullRetryIn', { s: left }) : t('money.fullRetrying'),
          ],
        };
      }
      const frozen = frozenLifeLine(m);
      return {
        ...base,
        mode: 'error',
        status: frozen !== null ? [frozen] : [],
        error: moneyErrorText(m.flow.error),
      };
    }
    default: {
      const n = m.roomPlayers ?? m.flow.roomHumans;
      const lines = [t('money.inRoom', { stake: formatStake(stake ?? 0) })];
      if (n !== null) lines.push(t('money.roomPlayers', { n }));
      const frozen = frozenLifeLine(m);
      if (frozen !== null && step !== 'inLife') lines.push(frozen);
      if (step === 'inLife') {
        lines.push(
          m.touch ? t('money.holdTouch') : t('money.inLife', { key: m.cashOutKey || 'Q' }),
        );
      }
      return { ...base, mode: 'room', status: lines };
    }
  }
}

/** The buy-in line under Play in a money arena: "Buy-in $1 · you start with $0.90". */
export function buyInText(stakeUsd: number, rake: number): string {
  return t('money.buyIn', { stake: formatStake(stakeUsd), value: entryValueText(stakeUsd, rake) });
}

export interface MoneyCardDeps {
  readonly flow: ReadonlySignal<MoneyFlowState>;
  /** GET /api/info.money (null when money rooms are off or the request failed). */
  fetchInfo(): Promise<ApiMoneyInfo | null>;
  /** app.openMoney(): false when money rooms are off. */
  open(): Promise<boolean>;
  pickStake(usd: number): Promise<void>;
  back(): void;
  leave(): void;
  lifeActive(): boolean;
  roomPlayers(): number | null;
  cashOutKey(): string;
  /** Touch device (touch copy); absent = false. */
  touch?(): boolean;
  /** Formats lamports in the label currency (the frozen-life line). */
  formatValue?(lamports: number): string;
  playClick?(source: UiClickSource): void;
  /** Sign-in (the account row + the stake gate); absent = no row, stakes need no account. */
  readonly account?: MoneyCardAccountDeps;
}

export interface MoneyCardAccountDeps extends Omit<
  AccountRowDeps,
  'moneyLifeActive' | 'playClick'
> {
  /**
   * Before a stake joins: signed in with a username (asks for both as needed). Resolves false
   * when the player backed out (nothing happens then).
   */
  requireAccount(): Promise<boolean>;
}

export interface MoneyCard {
  readonly el: HTMLElement;
  /** The card is on screen (home): polls /api/info.money while true. */
  setActive(active: boolean): void;
  /** Re-renders (the app calls it when the room head count changes). */
  refresh(): void;
  /** The newest polled info (null before the first answer). */
  readonly info: ApiMoneyInfo | null;
  dispose(): void;
}

/** /api/info.money poll interval while the card is on screen. */
export const MONEY_POLL_MS = 15_000;

export function createMoneyCard(deps: MoneyCardDeps): MoneyCard {
  let info: ApiMoneyInfo | null = null;
  let active = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let busy = false;

  const tag = h('span', { class: 'money-tag', hidden: true }, t('money.test'));
  const soonTag = h('span', { class: 'money-tag money-soon', hidden: true }, t('money.soon'));
  let loaded = false;
  let tickTimer: ReturnType<typeof setInterval> | null = null;
  const blurb = h('p', { class: 'money-blurb' });
  const list = h('div', { class: 'money-stakes' });
  const status = h('div', { class: 'money-status', role: 'status' });
  const spinner = h('span', { class: 'ring-spinner ring-spinner-sm', 'aria-hidden': 'true' });
  const error = h('p', { class: 'money-error', role: 'alert', hidden: true });
  const otherBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn-small btn-quiet',
      onClick: () => {
        deps.playClick?.('stake');
        deps.back();
      },
    },
    t('money.otherStake'),
  );
  const leaveBtn = h(
    'button',
    {
      type: 'button',
      class: 'btn-small btn-quiet',
      onClick: () => {
        if (leaveBtn.getAttribute('aria-disabled') === 'true') return;
        deps.playClick?.('stake');
        deps.leave();
      },
    },
    t('money.leave'),
  );
  const actions = h('div', { class: 'money-actions' }, otherBtn, leaveBtn);
  const accountDeps = deps.account;
  const accountRow: AccountRow | null = accountDeps
    ? createAccountRow({
        ...accountDeps,
        moneyLifeActive: () => deps.lifeActive(),
        ...(deps.playClick ? { playClick: deps.playClick } : {}),
      })
    : null;
  const el = h(
    'section',
    { class: 'card money-card', hidden: true },
    h('h2', { class: 'card-title money-title' }, t('money.title'), tag, soonTag),
    blurb,
    list,
    status,
    spinner,
    error,
    actions,
    accountRow?.el ?? null,
  );

  const pick = async (usd: number): Promise<void> => {
    if (busy) return;
    busy = true;
    try {
      deps.playClick?.('stake');
      if (accountDeps && !(await accountDeps.requireAccount())) return;
      const step = deps.flow.get().step;
      if (step === 'off' || step === 'error') {
        const ok = await deps.open();
        if (!ok) return;
      }
      await deps.pickStake(usd);
    } finally {
      busy = false;
      render();
    }
  };

  function render(): void {
    const view = moneyCardView({
      flow: deps.flow.get(),
      info,
      loaded,
      roomPlayers: deps.roomPlayers(),
      lifeActive: deps.lifeActive(),
      cashOutKey: deps.cashOutKey(),
      touch: deps.touch?.() ?? false,
      ...(deps.formatValue ? { formatValue: (v: number) => deps.formatValue?.(v) ?? '' } : {}),
      nowMs: typeof performance !== 'undefined' ? performance.now() : 0,
    });
    show(el, view.mode !== 'hidden');
    el.dataset['mode'] = view.mode;
    show(tag, view.test);
    show(soonTag, view.soon);
    blurb.textContent = view.blurb;
    show(blurb, view.mode === 'list' || view.mode === 'soon');
    show(list, view.mode === 'list');
    if (view.mode === 'list') {
      list.replaceChildren(
        ...view.stakes.map((s) =>
          h(
            'button',
            {
              type: 'button',
              class: `money-stake${s.selected ? ' is-selected' : ''}`,
              'aria-disabled': s.full || busy ? 'true' : 'false',
              onClick: () => {
                if (s.full) return;
                void pick(s.usd);
              },
            },
            h('span', { class: 'money-stake-label' }, s.label),
            h('span', { class: 'money-stake-players' }, s.players),
          ),
        ),
      );
    }
    status.replaceChildren(...view.status.map((line) => h('p', null, line)));
    show(status, view.status.length > 0);
    show(spinner, view.mode === 'joining' || view.mode === 'waiting');
    error.textContent = view.error ?? '';
    show(error, view.error !== null);
    const waiting = view.mode === 'waiting';
    show(actions, view.mode === 'room' || view.mode === 'error' || waiting);
    show(otherBtn, view.mode === 'room' || view.mode === 'error' || waiting);
    otherBtn.textContent = waiting ? t('money.cancel') : t('money.otherStake');
    show(leaveBtn, !waiting);
    leaveBtn.setAttribute('aria-disabled', view.canLeave ? 'false' : 'true');
    accountRow?.refresh();
    // The full-retry countdown ticks while it is on screen.
    if (waiting && tickTimer === null) tickTimer = setInterval(render, 500);
    else if (!waiting && tickTimer !== null) {
      clearInterval(tickTimer);
      tickTimer = null;
    }
  }

  const poll = (): void => {
    void deps
      .fetchInfo()
      .catch(() => null)
      .then((next) => {
        info = next;
        loaded = true;
        render();
      });
  };

  const offFlow: Unsubscribe = deps.flow.subscribe(() => render());
  render();

  return {
    el,
    get info() {
      return info;
    },
    setActive(on: boolean): void {
      if (on === active) return;
      active = on;
      if (timer !== null) clearInterval(timer);
      timer = null;
      if (on) {
        poll();
        timer = setInterval(poll, MONEY_POLL_MS);
      }
    },
    refresh: render,
    dispose(): void {
      offFlow();
      accountRow?.dispose();
      if (timer !== null) clearInterval(timer);
      timer = null;
      if (tickTimer !== null) clearInterval(tickTimer);
      tickTimer = null;
    },
  };
}
